import React, { useMemo, useState, useCallback } from 'react';
import { FaProjectDiagram, FaPlus, FaGripVertical, FaExternalLinkAlt } from 'react-icons/fa';
import { ProjectTimelineItem } from '../services/PlantafelService';

interface PlantafelProjectsTimelineProps {
  projects: ProjectTimelineItem[];
  weekStart: Date;
  days: { date: Date; dateStr: string; label: string }[];
  colWidth: number;
  labelWidth: number;
  selectedProjectId: number | null;
  onProjectClick: (projectId: number | null) => void;
  onCreateProject?: () => void;
  // FE-OPS-R22-2: duration parameter for preserving existing project duration
  onDropProject?: (projectId: number, dateStr: string, durationDays?: number) => void;
  // All available projects for showing empty lanes
  allProjects?: ProjectTimelineItem[];
  // View mode for different column representations
  viewMode?: 'week' | 'month' | 'year' | 'custom';
  // FE-OPS-R24-3: Navigate to project dossier page
  onOpenProject?: (projectId: number) => void;
}

const ROW_HEIGHT = 36;
const BAR_HEIGHT = 28;

// Project color palette - Offer Funnel colors from Sales Dashboard
const PROJECT_COLORS: Array<{ bg: string; text: string }> = [
  { bg: '#10b981', text: '#ffffff' },  // Green (Accepted)
  { bg: '#7c3aed', text: '#ffffff' },  // Purple (Sent)
  { bg: '#021a4a', text: '#ffffff' },  // Navy (Draft)
  { bg: '#ec4899', text: '#ffffff' },  // Pink (Rejected)
  { bg: '#fbbf24', text: '#000000' },  // Yellow (Expired)
];

const DEFAULT_COLOR = { bg: '#f3f4f6', border: '#9ca3af' };

export default function PlantafelProjectsTimeline({
  projects,
  weekStart,
  days,
  colWidth,
  labelWidth,
  selectedProjectId,
  onProjectClick,
  onCreateProject,
  onDropProject,
  allProjects = [],
  viewMode = 'week',
  onOpenProject,
}: PlantafelProjectsTimelineProps): React.JSX.Element {
  // Calculate range based on view mode
  const rangeStartMs = weekStart.getTime();
  const lastDay = days[days.length - 1];

  // For year view, each column is a week (7 days)
  // For week/month view, each column is a day
  const msPerColumn = viewMode === 'year' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

  const rangeEndMs = lastDay
    ? new Date(lastDay.dateStr).getTime() + msPerColumn
    : rangeStartMs + days.length * msPerColumn;
  const totalWidth = days.length * colWidth;

  // Track which row is being hovered for drop
  const [dropTargetProjectId, setDropTargetProjectId] = useState<number | null>(null);
  // Track general drop area for new project lane
  const [isNewLaneDropTarget, setIsNewLaneDropTarget] = useState(false);

  // Calculate day index from x position
  const getDayIndexFromX = useCallback((x: number): number => {
    return Math.floor(x / colWidth);
  }, [colWidth]);

  // Merge all projects and projects with timeline data
  // Each project in allProjects gets its own lane, with bars if they have timeline data
  const projectsWithLanes = useMemo(() => {
    // Create a map of projects with timeline data
    const timelineMap = new Map<number, ProjectTimelineItem>();
    projects.forEach(p => {
      if (p.timeline_start || p.timeline_end) {
        timelineMap.set(p.id, p);
      }
    });

    // Use allProjects as the base (all projects get lanes)
    // If allProjects is empty, fall back to projects
    const baseProjects = allProjects.length > 0 ? allProjects : projects;

    return baseProjects.map(project => {
      const timelineProject = timelineMap.get(project.id);
      return {
        ...project,
        hasTimeline: !!timelineProject,
        timeline_start: timelineProject?.timeline_start || project.timeline_start,
        timeline_end: timelineProject?.timeline_end || project.timeline_end,
        timeline_source: timelineProject?.timeline_source || project.timeline_source,
      };
    });
  }, [projects, allProjects]);

  // Calculate bar position for a project
  const getProjectBar = useCallback((project: ProjectTimelineItem) => {
    if (!project.timeline_start && !project.timeline_end) return null;

    const startMs = project.timeline_start
      ? new Date(project.timeline_start).getTime()
      : rangeStartMs;
    const endMs = project.timeline_end
      ? new Date(project.timeline_end).getTime() + 24 * 60 * 60 * 1000
      : rangeEndMs;

    // Skip if project is completely outside the current range
    if (endMs < rangeStartMs || startMs > rangeEndMs) {
      return null;
    }

    const clampedStartMs = Math.max(startMs, rangeStartMs);
    const clampedEndMs = Math.min(endMs, rangeEndMs);

    const leftRatio = (clampedStartMs - rangeStartMs) / (rangeEndMs - rangeStartMs);
    const rightRatio = (clampedEndMs - rangeStartMs) / (rangeEndMs - rangeStartMs);

    const left = leftRatio * totalWidth;
    const width = Math.max((rightRatio - leftRatio) * totalWidth, 40);

    return {
      left,
      width,
      clampedLeft: startMs < rangeStartMs,
      clampedRight: endMs > rangeEndMs,
    };
  }, [rangeStartMs, rangeEndMs, totalWidth]);

  // Drag handlers for project drops - per row
  const handleRowDragOver = useCallback((e: React.DragEvent, projectId: number) => {
    const types = Array.from(e.dataTransfer.types);
    const hasProjectId = types.some((t) => t.toLowerCase() === 'projectid');
    if (hasProjectId && onDropProject) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [onDropProject]);

  const handleRowDragEnter = useCallback((e: React.DragEvent, projectId: number) => {
    const types = Array.from(e.dataTransfer.types);
    const hasProjectId = types.some((t) => t.toLowerCase() === 'projectid');
    if (hasProjectId && onDropProject) {
      e.preventDefault();
      setDropTargetProjectId(projectId);
    }
  }, [onDropProject]);

  const handleRowDragLeave = useCallback((e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropTargetProjectId(null);
    }
  }, []);

  const handleRowDrop = useCallback((e: React.DragEvent, targetProjectId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetProjectId(null);

    const projectId = e.dataTransfer.getData('projectId');
    if (!projectId || !onDropProject) return;

    // FE-OPS-R22-2: Get existing duration if available
    const plannedDurationStr = e.dataTransfer.getData('plannedDuration');
    const durationDays = plannedDurationStr ? parseInt(plannedDurationStr, 10) : undefined;

    // Calculate drop position relative to timeline
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const dayIndex = Math.max(0, Math.min(days.length - 1, getDayIndexFromX(x)));
    const dateStr = days[dayIndex]?.dateStr;

    if (dateStr) {
      onDropProject(parseInt(projectId, 10), dateStr, durationDays);
    }
  }, [onDropProject, days, getDayIndexFromX]);

  // Handlers for the "new lane" drop zone at bottom
  const handleNewLaneDragOver = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    const hasProjectId = types.some((t) => t.toLowerCase() === 'projectid');
    if (hasProjectId && onDropProject) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [onDropProject]);

  const handleNewLaneDragEnter = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    const hasProjectId = types.some((t) => t.toLowerCase() === 'projectid');
    if (hasProjectId && onDropProject) {
      e.preventDefault();
      setIsNewLaneDropTarget(true);
    }
  }, [onDropProject]);

  const handleNewLaneDragLeave = useCallback((e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsNewLaneDropTarget(false);
    }
  }, []);

  const handleNewLaneDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNewLaneDropTarget(false);

    const projectId = e.dataTransfer.getData('projectId');
    if (!projectId || !onDropProject) return;

    const plannedDurationStr = e.dataTransfer.getData('plannedDuration');
    const durationDays = plannedDurationStr ? parseInt(plannedDurationStr, 10) : undefined;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - labelWidth;
    const dayIndex = Math.max(0, Math.min(days.length - 1, getDayIndexFromX(x)));
    const dateStr = days[dayIndex]?.dateStr;

    if (dateStr) {
      onDropProject(parseInt(projectId, 10), dateStr, durationDays);
    }
  }, [onDropProject, days, getDayIndexFromX, labelWidth]);

  return (
    <div style={{ background: '#fafbfc', borderBottom: '2px solid #d0d3df' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid #e0e3ef',
          background: '#f0f4ff',
        }}
      >
        <div
          style={{
            width: labelWidth,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#5b21b6',
          }}
        >
          <FaProjectDiagram size={12} />
          Projekte
          {selectedProjectId && (
            <button
              onClick={() => onProjectClick(null)}
              style={{
                padding: '2px 6px',
                border: '1px solid #e0e3ef',
                borderRadius: '4px',
                background: 'white',
                fontSize: '10px',
                color: '#666',
                cursor: 'pointer',
              }}
            >
              Filter aufheben
            </button>
          )}
          {onCreateProject && (
            <button
              onClick={onCreateProject}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                border: 'none',
                borderRadius: '4px',
                background: '#6366f1',
                fontSize: '10px',
                fontWeight: 600,
                color: 'white',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
            >
              <FaPlus size={9} />
              Projekt
            </button>
          )}
        </div>
      </div>

      {/* Project Lanes - one per project */}
      {projectsWithLanes.length === 0 ? (
        <div
          onDragOver={handleNewLaneDragOver}
          onDragEnter={handleNewLaneDragEnter}
          onDragLeave={handleNewLaneDragLeave}
          onDrop={handleNewLaneDrop}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            background: isNewLaneDropTarget ? '#e0f2fe' : '#fafbfc',
            borderBottom: '1px solid #e0e3ef',
            color: '#888',
            fontSize: '13px',
            transition: 'background 0.15s',
          }}
        >
          <FaProjectDiagram size={14} style={{ marginRight: '8px' }} />
          {isNewLaneDropTarget ? 'Projekt hier ablegen um zu planen' : 'Keine Projekte vorhanden - ziehen Sie ein Projekt aus dem linken Panel'}
        </div>
      ) : (
        projectsWithLanes.map((project, index) => {
          const bar = getProjectBar(project);
          const isSelected = selectedProjectId === project.id;
          const isDropTarget = dropTargetProjectId === project.id;
          const colors = PROJECT_COLORS[index % PROJECT_COLORS.length];

          return (
            <div
              key={project.id}
              style={{
                display: 'flex',
                height: ROW_HEIGHT,
                borderBottom: '1px solid #e8eaef',
                background: isDropTarget ? '#e0f2fe' : isSelected ? '#f0f4ff' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              {/* Project Label */}
              <div
                style={{
                  width: labelWidth,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '0 10px',
                  borderRight: '1px solid #e0e3ef',
                  background: isSelected ? '#f0f4ff' : '#fafbfc',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  cursor: 'pointer',
                }}
                onClick={() => onProjectClick(isSelected ? null : project.id)}
                title={`${project.partner_name ? project.partner_name + ' – ' : ''}${project.name}`}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: colors.bg,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: isSelected ? '#5b21b6' : '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}
                >
                  {project.name}
                </span>
                {project.partner_name && (
                  <span
                    style={{
                      fontSize: '9px',
                      color: '#888',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '60px',
                    }}
                  >
                    {project.partner_name}
                  </span>
                )}
              </div>

              {/* Timeline Area */}
              <div
                onDragOver={(e) => handleRowDragOver(e, project.id)}
                onDragEnter={(e) => handleRowDragEnter(e, project.id)}
                onDragLeave={handleRowDragLeave}
                onDrop={(e) => handleRowDrop(e, project.id)}
                style={{
                  position: 'relative',
                  width: totalWidth,
                  height: '100%',
                }}
              >
                {/* Day grid lines */}
                {days.map((day, idx) => (
                  <div
                    key={day.dateStr}
                    style={{
                      position: 'absolute',
                      left: idx * colWidth,
                      top: 0,
                      bottom: 0,
                      width: colWidth,
                      borderRight: '1px solid #e8eaef',
                    }}
                  />
                ))}

                {/* Project Bar (if has timeline) - draggable like Jobs */}
                {bar && (
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('projectId', project.id.toString());
                      e.dataTransfer.setData('projectName', project.name);
                      // Calculate duration in days for preserving on drop
                      if (project.timeline_start && project.timeline_end) {
                        const durationDays = Math.ceil(
                          (new Date(project.timeline_end).getTime() - new Date(project.timeline_start).getTime()) / (1000 * 60 * 60 * 24)
                        ) + 1;
                        e.dataTransfer.setData('plannedDuration', durationDays.toString());
                      }
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onClick={(e) => {
                      // Shift+click: toggle filter (existing behavior)
                      // Regular click: open Projektakte
                      if (e.shiftKey) {
                        onProjectClick(isSelected ? null : project.id);
                      } else if (onOpenProject) {
                        onOpenProject(project.id);
                      } else {
                        onProjectClick(isSelected ? null : project.id);
                      }
                    }}
                    title={`${project.partner_name ? project.partner_name + ' – ' : ''}${project.name}\n${project.timeline_start || '?'} → ${project.timeline_end || '?'}${project.timeline_source === 'shifts' ? ' (aus Jobs)' : ''}\nKlick: Projektakte öffnen | Shift+Klick: Filter | Ziehen: Verschieben`}
                    style={{
                      position: 'absolute',
                      top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                      left: bar.left,
                      width: bar.width,
                      height: BAR_HEIGHT,
                      background: colors.bg,
                      border: project.timeline_source === 'shifts' ? '2px dashed rgba(255,255,255,0.5)' : 'none',
                      borderRadius: '6px',
                      cursor: 'grab',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      overflow: 'hidden',
                      boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.15)',
                      opacity: isSelected ? 1 : 0.9,
                      transition: 'all 0.15s',
                      zIndex: isSelected ? 5 : 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: colors.text,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                      }}
                    >
                      {project.partner_name && (
                        <span style={{ fontWeight: 400, opacity: 0.8 }}>
                          {project.partner_name}
                          {project.city && ` (${project.city})`}
                          {' – '}
                        </span>
                      )}
                      {project.name}
                    </span>
                    {project.milestones_ready_count !== undefined && project.milestones_ready_count > 0 && (
                      <span
                        style={{
                          flexShrink: 0,
                          marginLeft: '6px',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '9px',
                          fontWeight: 700,
                          background: 'rgba(255,255,255,0.25)',
                          color: 'white',
                        }}
                        title={`${project.milestones_ready_count} Meilenstein(e) bereit`}
                      >
                        {project.milestones_ready_count}
                      </span>
                    )}
                  </div>
                )}

                {/* Drop indicator when no bar and hovering */}
                {!bar && isDropTarget && (
                  <div
                    style={{
                      position: 'absolute',
                      top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '4px 12px',
                      background: '#dbeafe',
                      border: '2px dashed #3b82f6',
                      borderRadius: '6px',
                      fontSize: '10px',
                      color: '#3b82f6',
                      fontWeight: 500,
                    }}
                  >
                    Hier ablegen
                  </div>
                )}

                {/* Placeholder for unscheduled projects (not hovering) */}
                {!bar && !isDropTarget && (
                  <div
                    style={{
                      position: 'absolute',
                      top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                      left: 12,
                      padding: '4px 10px',
                      background: '#f8f9fb',
                      border: '1px dashed #d0d3df',
                      borderRadius: '6px',
                      fontSize: '10px',
                      color: '#999',
                    }}
                  >
                    Noch nicht geplant
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Drop zone for adding new project scheduling */}
      {projectsWithLanes.length > 0 && (
        <div
          onDragOver={handleNewLaneDragOver}
          onDragEnter={handleNewLaneDragEnter}
          onDragLeave={handleNewLaneDragLeave}
          onDrop={handleNewLaneDrop}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 16px',
            background: isNewLaneDropTarget ? '#e0f2fe' : '#f8f9fb',
            borderTop: '1px dashed #d0d3df',
            color: isNewLaneDropTarget ? '#3b82f6' : '#aaa',
            fontSize: '11px',
            transition: 'all 0.15s',
            minHeight: '28px',
          }}
        >
          <FaPlus size={10} style={{ marginRight: '6px' }} />
          {isNewLaneDropTarget ? 'Projekt hier ablegen' : 'Projekt aus dem linken Panel hierher ziehen'}
        </div>
      )}
    </div>
  );
}
